import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { getIconByName } from "@/lib/amenityIcons";
import { Attribute } from "@/lib/types"; // Use Attribute type
import { Check } from "lucide-react";
import { useMemo } from "react";

interface PropertyAmenitiesSectionProps {
  amenities: Attribute[]; // Expect Attribute[] directly
}

const PropertyAmenitiesSection = ({
  amenities,
}: PropertyAmenitiesSectionProps) => {
  // Calculate normalizedAmenities using useMemo
  const normalizedAmenities = useMemo(() => {
    // Return early if amenities are empty
    if (!amenities || amenities.length === 0) {
      return [];
    }

    // Filter for amenities (type === "amenity")
    const filteredAmenities = amenities.filter(
      (attr) => attr.type === "amenity"
    );

    if (import.meta.env.DEV) {
      console.log("Amenities prop in PropertyAmenitiesSection:", amenities);
      console.log(
        "Filtered amenities in PropertyAmenitiesSection:",
        filteredAmenities
      );
    }

    return filteredAmenities;
  }, [amenities]); // Depend only on amenities prop

  // If there are no amenities, don't render this section
  if (!amenities || amenities.length === 0) {
    return null;
  }

  // Removed isLoading check as useAmenities is no longer used
  // if (isLoading) {
  //   return (
  //     <Card className="overflow-hidden border border-propertylk-blue/10">
  //       <CardHeader className="bg-gradient-to-r from-propertylk-lightblue to-white border-b border-propertylk-blue/10">
  //         <div className="flex items-center gap-3">
  //           <div className="p-2 rounded-full bg-propertylk-lightblue text-propertylk-blue">
  //             <Check className="w-4 h-4" />
  //           </div>
  //           <CardTitle className="text-propertylk-darkblue">
  //             Amenities & Features
  //           </CardTitle>
  //         </div>
  //       </CardHeader>
  //       <CardContent className="p-4">
  //         <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
  //           {Array(6)
  //             .fill(0)
  //             .map((_, index) => (
  //               <Skeleton key={index} className="h-8 w-full" />
  //             ))}
  //         </div>
  //       </CardContent>
  //     </Card>
  //   );
  // }

  if (normalizedAmenities.length === 0) {
    return null;
  }

  return (
    <Card className="overflow-hidden border border-propertylk-blue/10">
      <CardHeader className="bg-gradient-to-r from-propertylk-lightblue to-white border-b border-propertylk-blue/10">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-propertylk-lightblue text-propertylk-blue">
            <Check className="w-4 h-4" />
          </div>
          <CardTitle className="text-propertylk-darkblue">
            Amenities & Features
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
          {normalizedAmenities.map((amenity, index) => {
            if (!amenity) {
              return null;
            }

            const Icon = getIconByName(amenity.icon);

            return (
              <div key={amenity.id || index} className="flex items-center">
                <div className="mr-2 p-1 rounded-full bg-propertylk-lightblue text-propertylk-blue">
                  {Icon ? (
                    <Icon className="w-4 h-4" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                </div>
                <span className="text-gray-700">{amenity.name}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertyAmenitiesSection;
